/**
 * Shared frame + parts for Tarii's own sign-in and sign-up pages
 * (3 Oct 2026). Clerk still does the authenticating (`useSignIn` /
 * `useSignUp` in the pages); every pixel here is ours — the app's tokens
 * and `ui/*` primitives, the landing's typeface, no Clerk branding.
 *
 * Register: one calm column, the Tarii lockup, one heading and one line
 * of copy, then the step's controls. Inputs are 44 px tall with 16 px
 * text so a phone neither misses a tap nor zooms on focus.
 */
import {
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";

import { Logo } from "../../components/Logo";
import { Button, Input, Label } from "../../components/ui";
import { useAuth } from "../../lib/AuthContext";
import { ADD_ACCOUNT_PATH } from "../../lib/accountSwitch";
import { cn } from "../../lib/cn";
import {
  claimAutoForward,
  consumeReturnTo,
  peekFreshReturnTo,
  returnTargetFromSearch,
  safeReturnTarget,
} from "../../lib/returnTo";
import type { FriendlyAuthError } from "./clerkErrors";
import "../marketing/landing-palette.css";
import "./auth.css";

/** The one line under the heading — the landing's overview line (industry-neutral). */
export const AUTH_LEAD_LINE = "Rostering your team can trust.";

/**
 * The page frame. `title` is the step's heading; it takes focus whenever
 * it changes so a screen-reader user hears the new step (the heading is
 * the first thing on the step, and the first field is one Tab away).
 */
export function AuthFrame({
  title,
  lead,
  children,
  footer,
}: {
  title: string;
  lead: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstTitle = useRef(title);
  useEffect(() => {
    // Not on first paint (that would steal focus from the page), only
    // when a step change swaps the heading.
    if (title !== firstTitle.current) headingRef.current?.focus();
  }, [title]);

  return (
    <div data-auth-root="" className="min-h-[100svh] bg-bg text-fg">
      <div className="mx-auto flex min-h-[100svh] w-full max-w-[400px] flex-col px-4 pt-8 pb-10 sm:justify-center sm:px-6 sm:py-16">
        <Link
          to="/"
          aria-label="Tarii home"
          className="self-start rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-4 focus-visible:ring-offset-bg"
        >
          <Logo className="h-8" />
        </Link>
        <main className="mt-10 sm:mt-12">
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="font-semibold text-[1.75rem] text-fg leading-[1.15] tracking-[-0.01em] focus:outline-none"
          >
            {title}
          </h1>
          <div className="mt-2 text-[0.9375rem] text-fg-muted leading-relaxed">{lead}</div>
          <div className="mt-8">{children}</div>
        </main>
        {footer ? (
          <footer className="mt-10 border-border border-t pt-5 text-[0.9375rem] text-fg-muted">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}

/** Google's "G", drawn inline (Google's sign-in guidelines ask for it). */
function GoogleMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 18 18" width="18" height="18" className="shrink-0">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

export function GoogleButton({
  onClick,
  busy,
  disabled,
}: {
  onClick: () => void;
  busy: boolean;
  disabled: boolean;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className="h-11 w-full text-[0.9375rem]"
      onClick={onClick}
      disabled={disabled}
      aria-busy={busy || undefined}
    >
      <GoogleMark />
      {busy ? "Opening Google…" : "Continue with Google"}
    </Button>
  );
}

export function OrDivider() {
  return (
    <div className="my-6 flex items-center gap-3" aria-hidden="true">
      <span className="h-px flex-1 bg-border" />
      <span className="font-mono text-[10px] text-fg-subtle uppercase tracking-[0.18em]">or</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

/** A labelled input, 44 px tall with 16 px text (no zoom-on-focus on a phone). */
export const AuthField = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { id: string; label: string }
>(({ id, label, className, ...props }, ref) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={id} className="text-fg">
      {label}
    </Label>
    <Input ref={ref} id={id} className={cn("h-11 text-base", className)} {...props} />
  </div>
));
AuthField.displayName = "AuthField";

/** The step's one primary action. */
export function SubmitButton({
  busy,
  busyLabel,
  children,
  disabled,
}: {
  busy: boolean;
  busyLabel: string;
  children: ReactNode;
  disabled: boolean;
}) {
  return (
    <Button
      type="submit"
      className="h-11 w-full text-[0.9375rem]"
      disabled={disabled}
      aria-busy={busy || undefined}
    >
      {busy ? busyLabel : children}
    </Button>
  );
}

/**
 * A quiet text action — secondary paths (code instead, forgot password,
 * resend) are links in weight, buttons in semantics when they don't
 * navigate.
 */
export function QuietAction({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-sm text-accent-fg text-sm underline underline-offset-4 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export const quietLinkClass =
  "rounded-sm text-accent-fg underline underline-offset-4 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

/**
 * The step's error, in one sentence, announced on arrival. `actions` is
 * the follow-up the sentence suggests (create an account, reset, …).
 */
export function AuthAlert({ error, actions }: { error: FriendlyAuthError; actions?: ReactNode }) {
  return (
    <div
      role="alert"
      data-error-code={error.code ?? undefined}
      className="rounded-md border border-danger/40 bg-danger-soft px-3.5 py-3 text-fg text-sm leading-relaxed"
    >
      <p>{error.message}</p>
      {actions ? <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">{actions}</div> : null}
    </div>
  );
}

/** A calm, non-error update ("We sent a new code"); `<output>` is a polite live region. */
export function AuthNotice({ children }: { children: ReactNode }) {
  return (
    <output className="block rounded-md border border-border bg-bg-subtle px-3.5 py-3 text-fg-muted text-sm">
      {children}
    </output>
  );
}

/**
 * A Clerk session already exists on this browser but Tarii has not
 * loaded an account for it (the gates bounce a loaded account before
 * these pages render) — e.g. the account request failed, or a second
 * sign-in was attempted in single-session mode (`session_exists`).
 * Offer the way on and the way out, never a dead end.
 *
 * #2240 — when the person was on their way somewhere (a deep link
 * bounced here: `?redirect_url=` on this URL, or a stash written in the
 * last few minutes — never one left from earlier in the tab), go on
 * there at once: a full load, so the account is asked for again, and a
 * replace, so Back does not return to this page. At most once per
 * `AUTO_FORWARD_WINDOW_MS` (a loop guard); otherwise the button does it.
 * Every address goes through `safeReturnTarget` right where it is used.
 * With nowhere to go on to, the button goes to `/` as before.
 */
export function AlreadySignedIn() {
  const auth = useAuth();
  const location = useLocation();
  const [leaving, setLeaving] = useState(false);
  const [target] = useState<string | null>(() =>
    safeReturnTarget(returnTargetFromSearch(location.search) ?? peekFreshReturnTo()),
  );
  useEffect(() => {
    if (!target || !claimAutoForward()) return;
    consumeReturnTo();
    const safe = safeReturnTarget(target);
    if (safe) window.location.replace(safe);
  }, [target]);
  return (
    <div className="flex flex-col gap-4">
      <Button
        type="button"
        className="h-11 w-full text-[0.9375rem]"
        onClick={() => {
          consumeReturnTo();
          window.location.assign(safeReturnTarget(target) ?? "/");
        }}
      >
        Continue to Tarii
      </Button>
      <p className="text-fg-muted text-sm">
        Not you?{" "}
        <QuietAction
          onClick={() => {
            setLeaving(true);
            // #2919 — through AuthContext, so the account caches clear.
            void auth.signOut({ redirectUrl: "/sign-in" });
          }}
          disabled={leaving}
        >
          {leaving ? "Signing out…" : "Sign out"}
        </QuietAction>
      </p>
      {/* #3005 — keep this account and sign in to another one too. */}
      <p className="text-fg-muted text-sm">
        <Link to={ADD_ACCOUNT_PATH} className={cn(quietLinkClass, "text-sm")}>
          Add another account
        </Link>
      </p>
    </div>
  );
}

/**
 * Clerk's bot protection mounts its CAPTCHA widget here (Cloudflare
 * Turnstile, "smart" mode: invisible unless the visitor looks automated,
 * then a one-click check). Clerk's custom-flow guide requires the element
 * to be in the DOM when `signUp.create()` runs — including the create
 * hidden inside `authenticateWithRedirect` and the OAuth transfer on
 * `/sso-callback` — or it silently falls back to an invisible widget that
 * can block a real person with no way to prove otherwise. Render it once
 * per page, outside the step switch, so React never replaces the node
 * Turnstile drew into.
 */
export function CaptchaSlot() {
  const theme =
    typeof document !== "undefined" &&
    ["dark", "pine", "still-water-night"].includes(document.documentElement.dataset.theme ?? "")
      ? "dark"
      : "light";
  // No margin until Turnstile actually draws something into the slot.
  return (
    <div
      id="clerk-captcha"
      data-cl-theme={theme}
      data-cl-size="flexible"
      className="has-[iframe]:mt-4"
    />
  );
}
